// src/pages/AdminsPage.js
import { useState, useEffect } from "react";
import { Container, Typography } from "@mui/material";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import CustomCheckbox from "../../components/CustomCheckbox";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import useLanguage from "../../utils/useLanguage";
import { useNavigate } from "react-router";
import ErrorBox from "../../components/ErrorBox";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import PageWrapper from "../../components/PageWrapper";

export default function AdminsPage() {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const [admins, setAdmins] = useState([]);
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  const [networkError, setNetworkError] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [adminModal, setAdminModal] = useState(false);

  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [loadingModal, setLoadingModal] = useState("");

  const [isEditing, setIsEditing] = useState(false);
  const [adminFields, setAdminFields] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [adminsCurrentPage, setAdminsCurrentPage] = useState(1);
  const [adminsTotalPages, setAdminsTotalPages] = useState(1);
  const [adminsTotalRecords, setAdminsTotalRecords] = useState(0);

  const [message, setMessage] = useState("");
  const [addAdminModal, setAddAdminModal] = useState(false);
  const [newAdminFields, setNewAdminFields] = useState({
    userName: "",
    adminEmail: "",
    password: "",
    confirmPassword: "",
    isSuperAdmin: false,
    superAdminPassword: "",
  });

  const navigate = useNavigate();

  useEffect(() => {
    getAdmins();
  }, [adminsCurrentPage, searchTerm]);

  const getAdmins = async () => {
    const token =
      localStorage.getItem("SuperAdminToken") ||
      localStorage.getItem("adminToken");
    const adminId = parseInt(localStorage.getItem("adminId"), 10);

    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getAdmins`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Admin-Id": adminId,
          },
          params: {
            page: adminsCurrentPage,
            limit: 5,
            search: searchTerm,
          },
        }
      );

      if (response.data && response.data.admins) {
        setAdmins(response.data.admins);
        setFilteredAdmins(response.data.admins);
        setAdminsTotalPages(response.data.totalPages);
        setAdminsTotalRecords(response.data.totalAdmins);
      } else {
        console.error("Failed to get the admins");
      }
      setLoading(false);
    } catch (error) {
      setNetworkError(true);
      console.error("Failed to get the admins", error);
      setLoading(false);
    }
  };

  const openAdminModal = (admin) => {
    setSelectedAdmin(admin);
    setAdminFields({
      userName: admin.username,
      adminEmail: admin.email,
    });
    setIsEditing(false);
    setAdminModal(true);
  };

  const handleInputChange = (e, fieldName) => {
    setAdminFields({
      ...adminFields,
      [fieldName]: e.target.value,
    });
  };

  const handleSaveAdmin = async () => {
    try {
      setLoadingModal("editAdmin");
      const response = await axios.put(
        `${process.env.REACT_APP_URL}/editAdmin`,
        {
          id: selectedAdmin?.id,
          username: adminFields.userName,
          email: selectedAdmin.email,
        }
      );

      if (response.data.message === "Admin updated successfully") {
        setAdminModal(false);
        await getAdmins();
      } else {
        console.error("Failed to update admin:", response.data.message);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error updating admin:", error);
      setLoading(false);
    } finally {
      setLoadingModal("");
    }
  };

  const handleCancel = () => {
    setRemoveAdminModal(false);
    setAdminModal(false);
    setSelectedAdmin(null);
  };

  const openRemoveAdminModal = (admin) => {
    setSelectedAdmin(admin);
    setRemoveAdminModal(true);
  };

  const [showRemoveAdminModal, setRemoveAdminModal] = useState(false);
  const handleDeleteAdmin = async () => {
    const token =
      localStorage.getItem("SuperAdminToken") ||
      localStorage.getItem("adminToken");
    console.log(token);

    try {
      setLoadingModal("deleteAdmin");
      const response = await axios.delete(
        `${process.env.REACT_APP_URL}/deleteAdmin/${selectedAdmin.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.message === "Admin deleted successfully") {
        setRemoveAdminModal(false);
        await getAdmins(); // Refresh the admin list
      } else {
        console.error("Failed to delete admin:", response.data.message);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error deleting admin:", error);
    } finally {
      setLoadingModal("");
      setAdminModal(false);
    }
  };

  const openAddAdminModal = () => {
    setAddAdminModal(true);
    setNewAdminFields({
      userName: "",
      adminEmail: "",
      password: "",
      confirmPassword: "",
      isSuperAdmin: false,
      superAdminPassword: "",
    });
  };

  const handleNewAdminInputChange = (e, fieldName) => {
    const value =
      fieldName === "isSuperAdmin" ? e.target.checked : e.target.value;
    setNewAdminFields((prevFields) => ({
      ...prevFields,
      [fieldName]: value,
    }));
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem("token");
      const adminId = parseInt(localStorage.getItem("adminId"), 10);

      // Password match validation
      if (newAdminFields.password !== newAdminFields.confirmPassword) {
        setError(true);
        setErrorMessage(t("passwordsDoNotMatch"));
        return;
      }

      if (
        !newAdminFields.adminEmail ||
        !newAdminFields.userName ||
        !newAdminFields.password ||
        !newAdminFields.superAdminPassword
      ) {
        setError(true);
        setErrorMessage(t("emptyInputFields"));
        return;
      }

      setLoadingModal("createAdmin");
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/createAdmin`,
        {
          username: newAdminFields.userName,
          email: newAdminFields.adminEmail,
          password: newAdminFields.password,
          isSuperAdmin: newAdminFields.isSuperAdmin,
          superAdminPassword: newAdminFields.superAdminPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Admin-Id": adminId,
          },
        }
      );

      setMessage(response.data.message);
      if (response.data.message === "Admin created successfully") {
        setError(false);
        setErrorMessage("");
        setAddAdminModal(false);
        setLoadingModal("");
        await getAdmins();
      }
      setLoading(false);
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        const errorKey = error.response.data.error;
        console.log("errorKey:", errorKey);
        // Map backend errors to user-friendly messages
        switch (errorKey) {
          case "emailAlreadyInUse":
            setError(true);
            setErrorMessage(t("emailAlreadyInUse"));
            break;
          case "invalidPhoneNumber":
            setError(true);
            setErrorMessage(t("invalidPhoneNumber"));
            break;
          default:
            setError(true);
            setErrorMessage(t("unexpectedError"));
            break;
        }
      } else {
        console.error("Network or other error:", error.message);
        setError(true);
        setErrorMessage(t("networkError"));
      }
    } finally {
      setLoading(false);
      setLoadingModal("");
    }
  };

  const handleCancelAddAdmin = () => {
    setError(false);
    setErrorMessage("");
    setAddAdminModal(false);
  };

  useEffect(() => {
    const userType = localStorage.getItem("userType");
    if (userType !== "superadmin") {
      navigate("/iraqi-banks-page");
    }
  }, []);

  return (
    <div className="e-container">
      <PageWrapper
          style={{ direction: currentDirection }}
          title={t("organizations")}
      >


        <div className="users-title-section">
          <h4 className="page--title">
            {t("admins", { defaultValue: "admins" })}
          </h4>

          <CustomButton
            title={t("createAdmin")}
            variant="blank"
            icon='addP'
            iconSize={20}
            onClick={openAddAdminModal}
          />
        </div>

        <CustomInput
          name="userName"
          value={searchTerm}
          placeholder={searchTerm ? "" : t("search")}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {!loading && networkError ? (
          <p color="error">{t("networkError")}</p>
        ) : !loading && filteredAdmins.length === 0 ? (
          <p>{t("noAdmins")}</p>
        ) : (
          !loading && (
            <ul className="users-list" style={{ direction: currentDirection }}>
              {filteredAdmins.map((admin) => (
                <li
                  className="user-list-item"
                  key={admin.id}
                  onClick={() => openAdminModal(admin)}
                  style={{ cursor: "pointer" }}
                >
                  <p className="user-username">
                    {admin.username || "No Name Available"}
                  </p>
                  {admin.email && (
                    <Typography variant="subtitle1" color="textSecondary">
                      {admin.email}
                    </Typography>
                  )}
                </li>
              ))}
            </ul>
          )
        )}

        {loadingModal === "" && loading && (
          <Loader size={LOADER_SIZES.MEDIUM} />
        )}

        <section className="pagination">
          <div className="pagination-buttons">
            <CustomButton
              variant="blank"
              title={t("previous")}
              onClick={() => setAdminsCurrentPage(adminsCurrentPage - 1)}
              disabled={adminsCurrentPage === 1}
              icon={currentDirection === "ltr" ? "previous" : "next"}
              iconSize={15}
            />
            <span className="pagination-location">
              {t("page")}: {adminsCurrentPage} - {adminsTotalPages}
            </span>
            <CustomButton
              title={t("next")}
              variant="blank"
              onClick={() => setAdminsCurrentPage(adminsCurrentPage + 1)}
              disabled={adminsCurrentPage === adminsTotalPages}
              icon={currentDirection === "ltr" ? "next" : "previous"}
              iconSize={15}
              reverseIcon
            />
          </div>

          <div className="pagination-buttons">
            <span className="pagination-location">
              {t("totalNumber")}: {adminsTotalRecords}
            </span>
          </div>
        </section>

        {adminModal && (
          <Modal
            title={isEditing ? t("editAdmin") : t("admin")}
            onClose={handleCancel}
            footer={
              <>
                {loadingModal === "editAdmin" && (
                  <Loader size={LOADER_SIZES.MEDIUM} />
                )}
                <section>
                  {isEditing ? (
                    <>
                      {/* {loadingModal === "editAdmin" ? <Loader /> : null} */}
                      <div className="edit-user-actions">
                        <CustomButton
                          title={t("confirm")}
                          onClick={handleSaveAdmin}
                          icon='save'
                          iconSize={16}
                          disabled={loadingModal === "editAdmin"}
                        />
                        <CustomButton
                          title={t("cancel")}
                          onClick={handleCancel}
                          variant="blank"
                          icon='close'
                          iconSize={14}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="edit-user-actions">
                      <CustomButton
                        variant="blank"
                        title={t("edit")}
                        onClick={() => setIsEditing(true)}
                        icon='edit'
                        iconSize={18}
                      />
                      <CustomButton
                        title={t("delete")}
                        variant="red"
                        icon='delete'
                        iconSize={18}
                        onClick={() => openRemoveAdminModal(selectedAdmin)}
                      />
                    </div>
                  )}
                </section>
              </>
            }
          >
            <div>
              <section>
                <div>
                  {isEditing ? (
                    <div className="edit-user-entity">
                      <span>{t("username")}: </span>
                      <CustomInput
                        name="userName"
                        value={adminFields.userName || ""}
                        onChange={(e) => handleInputChange(e, "userName")}
                      />
                    </div>
                  ) : (
                    <div className="view-user-entity">
                      <p>{t("username")}:</p>
                      <p>{selectedAdmin?.username}</p>
                    </div>
                  )}
                </div>
              </section>
              <section>
                <div>
                  {isEditing ? (
                    <div className="edit-user-entity">
                      <span>{t("email")}: </span>
                      <CustomInput
                        name="adminEmail"
                        value={adminFields.adminEmail || ""}
                        onChange={(e) => handleInputChange(e, "adminEmail")}
                      />
                    </div>
                  ) : (
                    <div className="view-user-entity">
                      <p>{t("email")}:</p>
                      <p>{selectedAdmin?.email}</p>
                    </div>
                  )}
                </div>
              </section>
            </div>
          </Modal>
        )}

        {showRemoveAdminModal && (
          <Modal
            title={t("deleteAdmin")}
            onClose={() => setRemoveAdminModal(false)}
            footer={
              <>
                {loadingModal === "deleteAdmin" ? (
                  <Loader size={LOADER_SIZES.MEDIUM} />
                ) : null}
                <section className="delete-user-actions">
                  <CustomButton
                    onClick={handleDeleteAdmin}
                    title={t("confirm")}
                    icon='save'
                    iconSize={16}
                    disabled={loadingModal === "deleteAdmin"}
                  />
                  <CustomButton
                    title={t("cancel")}
                    onClick={() => setRemoveAdminModal(false)}
                    variant="blank"
                    icon='close'
                    iconSize={14}
                  />
                </section>
              </>
            }
          >
            <div className="delete-modal-body">
              <h3>{t("areYouSureYouWantToDeleteThisUser")}</h3>
              <p>{selectedAdmin?.username || selectedAdmin?.email}</p>
            </div>
          </Modal>
        )}

        {/* Add Admin Modal */}
        {addAdminModal && (
          <Modal
            title={t("createAdmin")}
            onClose={handleCancelAddAdmin}
            footer={
              <>
                {loadingModal === "createAdmin" && (
                  <Loader size={LOADER_SIZES.MEDIUM} />
                )}
                <section className="edit-user-actions">
                  <CustomButton
                    title={t("confirm")}
                    onClick={handleCreateAdmin}
                    icon='save'
                    iconSize={16}
                    disabled={loadingModal === "createAdmin"}
                  />
                  <CustomButton
                    title={t("cancel")}
                    onClick={handleCancelAddAdmin}
                    variant="blank"
                    icon='close'
                    iconSize={14}
                  />
                </section>
              </>
            }
          >
            <form onSubmit={handleCreateAdmin}>
              <div>
                {error && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <ErrorBox>{errorMessage}</ErrorBox>
                  </div>
                )}
                <div className="edit-user-entity">
                  <span>{t("username")}: </span>
                  <CustomInput
                    name="userName"
                    value={newAdminFields.userName}
                    onChange={(e) => handleNewAdminInputChange(e, "userName")}
                  />
                </div>
                <div className="edit-user-entity">
                  <span>{t("email")}: </span>
                  <CustomInput
                    name="adminEmail"
                    type="email"
                    value={newAdminFields.adminEmail}
                    onChange={(e) => handleNewAdminInputChange(e, "adminEmail")}
                    required
                  />
                </div>
                <div className="edit-user-entity">
                  <span>{t("password")}: </span>
                  <CustomInput
                    name="password"
                    type="password"
                    value={newAdminFields.password}
                    onChange={(e) => handleNewAdminInputChange(e, "password")}
                    required
                  />
                </div>
                <div className="edit-user-entity">
                  <span>{t("confirmPassword")}: </span>
                  <CustomInput
                    name="confirmPassword"
                    type="password"
                    value={newAdminFields.confirmPassword}
                    onChange={(e) =>
                      handleNewAdminInputChange(e, "confirmPassword")
                    }
                    required
                  />
                </div>
                <div className="edit-user-entity">
                  <div className="user-row-section">
                    <CustomCheckbox
                      checked={newAdminFields.isSuperAdmin}
                      onChange={(e) =>
                        handleNewAdminInputChange(e, "isSuperAdmin")
                      }
                      color="primary"
                    />
                    <label>{t("isSuperAdmin")}</label>
                  </div>
                </div>
                <div className="edit-user-entity">
                  <span>{t("superAdminPassword")}: </span>
                  <CustomInput
                    name="superAdminPassword"
                    type="password"
                    value={newAdminFields.superAdminPassword}
                    onChange={(e) =>
                      handleNewAdminInputChange(e, "superAdminPassword")
                    }
                    required={newAdminFields.isSuperAdmin}
                  />
                </div>
              </div>
            </form>
          </Modal>
        )}
      </PageWrapper>
    </div>
  );
}
